<template>
  <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <RouterLink
      v-slot="{ navigate }"
      :to="{
        name: 'MobileLabourRecord',
        params: {
          labourId: labour.id
        },
        query: {
          day: `${dateAsYMD($props.selectedDate)}`
        }
      }"
      custom
    >
      <div
        class="flex items-center justify-between w-full p-4 hover:bg-gray-50 transition-colors duration-200"
        :class="[labourClass(labour)]"
        data-test="labour-row"
        @click="labour.dailyRecords($props.selectedDate).length === 0 ? navigate() : open = !open"
      >
        <div class="flex items-center space-x-3 flex-1">
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-blue-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>
          </div>

          <div class="flex-1">
            <p class="font-medium text-gray-900">
              {{ labour.first_name }} {{ labour.last_name }}
            </p>

            <p class="text-sm text-gray-500">
              {{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.recordCount', { count: labour.dailyRecords($props.selectedDate).length }) }}
            </p>
          </div>
        </div>

        <div class="flex items-center space-x-3">
          <div class="text-right">
            <p class="font-semibold text-gray-900">
              {{ $td(labour.dailyHours($props.selectedDate)) }}
            </p>

            <p class="text-xs text-gray-500">
              {{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.hours') }}
            </p>
          </div>

          <RouterLink
            v-if="$props.projectUnarchived"
            class="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-200"
            :to="{
              name: 'MobileLabourRecord',
              params: {
                labourId: labour.id
              },
              query: {
                day: `${dateAsYMD($props.selectedDate)}`
              }
            }"
            data-test="add-record"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 text-white">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>
          </RouterLink>

          <button
            v-if="labour.dailyRecords($props.selectedDate).length > 0"
            type="button"
            class="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-600 transition-transform duration-200"
              :class="{ 'rotate-180': open }">
  <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
</svg>
          </button>
        </div>
      </div>
    </RouterLink>

    <!-- Expanded Records -->
    <div
      v-if="open && labour.dailyRecords($props.selectedDate).length > 0"
      class="border-t border-gray-100"
      :class="[labourClass(labour)]"
      data-test="labour-record-table"
    >
      <div class="p-4">
        <h4 class="text-sm font-medium text-gray-700 mb-3">
          {{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.dailyRecords') }}
        </h4>

        <div class="space-y-2">
          <RouterLink
            v-for="record in labour.dailyRecords($props.selectedDate)"
            :key="record.id"
            class="block bg-white rounded-lg p-3 hover:bg-gray-50 transition-colors duration-200 border border-gray-200 relative"
            :to="{
              name: 'MobileLabourRecord',
              params: {
                labourId: labour.id,
                recordId: record.id
              }
            }"
          >
            <!-- Break Indicators -->
            <div class="absolute top-2 right-2 flex space-x-1">
              <div
                v-if="record.minor_break"
                class="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center"
                :title="$t('Mobile.MobileLabourDashboard.LabourRecordRow.smokeBreakTooltip')"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-3 h-3 text-orange-600">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>
              </div>

              <div
                v-if="record.major_break"
                class="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center"
                :title="$t('Mobile.MobileLabourDashboard.LabourRecordRow.majorBreakTooltip')"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-3 h-3 text-blue-600">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>
              </div>
            </div>

            <div class="flex items-center space-x-2">
              <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                {{ record.wbsCode.code }}
              </span>

              <span class="text-sm font-medium text-gray-900">
                {{ $td(record.hours) }}{{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.unit.hour') }}
              </span>
            </div>

            <!-- Hours Breakdown -->
            <div
              v-if="record.minor_break || record.major_break"
              class="flex justify-between items-center mt-2 text-xs text-gray-600"
            >
              <p>
                {{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.totalTime') }}:

                <span class="font-medium">{{ $td(record.hoursWithoutBreaks) }}{{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.unit.hour') }}</span>
              </p>

              <p
                v-if="record.minor_break || record.major_break"
              >
                {{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.breakTime') }}:

                <span class="font-medium text-orange-600">
                  {{ $td((record.minor_break ? 0.25 : 0) + (record.major_break ? 0.5 : 0)) }}{{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.unit.hour') }}
                </span>
              </p>
            </div>

            <span class="text-xs">
              {{ record.wbsCode.description }}
            </span>

            <div class="space-y-0.5">
              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.area') }}:</span>

                <span class="text-gray-900 font-medium">{{ record.area }}</span>
              </div>

              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.element') }}:</span>

                <span class="text-gray-900 font-medium">{{ record.element }}</span>
              </div>

              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ $t('Mobile.MobileLabourDashboard.LabourRecordRow.activity') }}:</span>

                <span class="text-gray-900 font-medium">{{ record.activity }}</span>
              </div>
            </div>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { dateAsYMD } from '@/helpers';
import Labour from '@/models/Labour';
import Project from '@/models/Project';
import { ref } from 'vue';

const props = defineProps<{
  labour: Labour;
  selectedDate: Date;
  project: Project;
  projectUnarchived: boolean;
}>();

const labourClass = (labour: Labour) => {
  if(labour.dailyHoursWithoutBreaks(props.selectedDate) >= props.project?.labour_standard_shift) {
    return 'border-green-200 bg-green-100';
  } else if(labour.dailyHoursWithoutBreaks(props.selectedDate) > 0) {
    return 'border-orange-200 bg-orange-100';
  } else {
    return 'border-red-200 bg-red-100';
  }
};

const open = ref(false);
</script>

<style scoped></style>
